import React, { useRef, useCallback, useMemo, useEffect, useState, useContext, Fragment } from "react";
import { useDispatch } from "react-redux";
import Editor from "@monaco-editor/react";

import api from "services/api";
import COMMON from "common";
import classNames from "common/class-names";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors from "common/serve-request-errors";
import AppButton from "components/app-button";
import AppEmptyState from "components/app-empty-state";
import AppLoadingIndicator from "components/app-loading-indicator";
import AppChevronIcon from "components/icons/app-chevron-icon";
import { AxiosContext } from "contexts/with-interceptor-provider";

const PREVIEW_DEBOUNCE_MS = 500;
const CATEGORY_ORDER = ["Account", "User", "Leave", "E-Learning", "Claim", "Others"];

const PageEmailTemplates = () => {
	const dispatch = useDispatch();
	const context = useContext(AxiosContext);
	const previewTimeoutRef = useRef();

	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);

	const [activeTemplateKey, setActiveTemplateKey] = useState("");
	const [expandedCategory, setExpandedCategory] = useState("");
	const [detailCache, setDetailCache] = useState({});
	const [drafts, setDrafts] = useState({});
	const [loadingDetail, setLoadingDetail] = useState(false);
	const [saving, setSaving] = useState(false);
	const [confirmingReset, setConfirmingReset] = useState(false);
	const [viewMode, setViewMode] = useState("preview");
	const [renderedHtml, setRenderedHtml] = useState("");
	const [loadingPreview, setLoadingPreview] = useState(false);

	const isEmptyState = useMemo(() => !loading && items.length === 0, [loading, items]);
	const groupedItems = useMemo(() => {
		const map = new Map();

		items.forEach((item) => {
			const category = item.category || "Others";
			if (!map.has(category)) map.set(category, []);
			map.get(category).push(item);
		});

		const ordered = CATEGORY_ORDER.filter((category) => map.has(category)).map((category) => ({ category, items: map.get(category) }));
		const extras = [...map.keys()].filter((category) => !CATEGORY_ORDER.includes(category)).map((category) => ({ category, items: map.get(category) }));

		return [...ordered, ...extras];
	}, [items]);
	const template = useMemo(() => detailCache[activeTemplateKey] || null, [detailCache, activeTemplateKey]);
	const html = useMemo(() => (drafts[activeTemplateKey] !== undefined ? drafts[activeTemplateKey] : template?.html || ""), [drafts, activeTemplateKey, template]);
	const isDirty = useMemo(() => !!template && html !== template.html, [template, html]);

	const isItemDirty = useCallback((key) => {
		const cached = detailCache[key];
		const draft = drafts[key];
		return !!cached && draft !== undefined && draft !== cached.html;
	}, [detailCache, drafts]);

	const onHandleGetList = useCallback(async () => {
		let response = null;

		try {
			response = await api.get.emailTemplate.list();
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) setItems(response);

		setLoading(false);
	}, []);

	const onHandleUpdateItem = useCallback((updatedTemplate) => {
		setItems((prev) => prev.map((item) => (item.templateKey === updatedTemplate.templateKey ? { ...item, ...updatedTemplate } : item)));
	}, []);

	const onHandleGetDetail = useCallback(async (key) => {
		let response = null;

		setLoadingDetail(true);

		try {
			response = await api.get.emailTemplate.detail(key);
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) setDetailCache((prev) => ({ ...prev, [key]: response }));

		setLoadingDetail(false);
	}, []);

	const onHandleToggleCategory = useCallback((category) => {
		setExpandedCategory((prev) => (prev === category ? "" : category));
	}, []);

	const onHandleSelectTemplate = useCallback((key) => {
		setActiveTemplateKey(key);
		setConfirmingReset(false);
		setViewMode("preview");

		if (!detailCache[key]) onHandleGetDetail(key);
	}, [detailCache, onHandleGetDetail]);

	const onHandleChangeHtml = useCallback((value) => {
		setDrafts((prev) => ({ ...prev, [activeTemplateKey]: value ?? "" }));
	}, [activeTemplateKey]);

	//prettier-ignore
	const onHandleConfirm = useCallback(async () => {
		let response = null;

		setSaving(true);

		try {
			response = await api.put.emailTemplate.update(activeTemplateKey, { html });
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			setSaving(false);
		}

		if (response) {
			const sanitized = response.html !== html;
			const key = activeTemplateKey;

			setDetailCache((prev) => ({ ...prev, [key]: response }));
			setDrafts((prev) => { const next = { ...prev }; delete next[key]; return next; });
			onHandleUpdateItem(response);
			setViewMode("preview");
			dispatch(promptAlertMessage({ message: sanitized ? "Saved — some content was removed for security (script/iframe tags and event-handler attributes aren't allowed)." : "Email template has been updated successfully" }));
		}
	}, [dispatch, activeTemplateKey, html, onHandleUpdateItem]);

	const onHandleToggleEdit = useCallback(() => {
		if (viewMode !== "editor") {
			setViewMode("editor");
		} else if (isDirty) {
			onHandleConfirm();
		} else {
			setViewMode("preview");
		}
	}, [viewMode, isDirty, onHandleConfirm]);

	const onHandleRequestReset = useCallback(() => {
		setConfirmingReset(true);
	}, []);

	const onHandleCancelEdit = useCallback(() => {
		const key = activeTemplateKey;

		setDrafts((prev) => { const next = { ...prev }; delete next[key]; return next; });
		setViewMode("preview");
	}, [activeTemplateKey]);

	const onHandleCancelReset = useCallback(() => {
		setConfirmingReset(false);
	}, []);

	//prettier-ignore
	const onHandleConfirmReset = useCallback(async () => {
		let response = null;
		const key = activeTemplateKey;

		setSaving(true);

		try {
			response = await api.post.emailTemplate.reset(key);
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			setSaving(false);
		}

		if (response) {
			setConfirmingReset(false);
			setDetailCache((prev) => ({ ...prev, [key]: response }));
			setDrafts((prev) => { const next = { ...prev }; delete next[key]; return next; });
			onHandleUpdateItem(response);
			dispatch(promptAlertMessage({ message: "Email template has been reset to default" }));
		}
	}, [dispatch, activeTemplateKey, onHandleUpdateItem]);

	useEffect(() => {
		onHandleGetList();
	}, [onHandleGetList]);

	useEffect(() => {
		if (!loading && items.length > 0 && !activeTemplateKey) {
			onHandleSelectTemplate(items[0].templateKey);
			setExpandedCategory(items[0].category || "Others");
		}
	}, [loading, items, activeTemplateKey, onHandleSelectTemplate]);

	useEffect(() => {
		if (!activeTemplateKey || !template) return undefined;

		clearTimeout(previewTimeoutRef.current);

		setLoadingPreview(true);

		previewTimeoutRef.current = setTimeout(async () => {
			let response = null;

			try {
				response = await api.post.emailTemplate.preview(activeTemplateKey, { html });
			} catch (error) {
				serveRequestErrors(error);
			}

			if (response) setRenderedHtml(response.renderedHtml);

			setLoadingPreview(false);
		}, PREVIEW_DEBOUNCE_MS);

		return () => clearTimeout(previewTimeoutRef.current);
	}, [activeTemplateKey, template, html]);

	useEffect(() => {
		return () => {
			context.onHandleCancelRequest(COMMON.ENDPOINT_PATH.EMAIL_TEMPLATE.LIST);
		};
	}, [context]);

	return (
		<div className="page-email-templates">
			<div className={classNames({ "email-templates": true, "email-templates--empty": isEmptyState })}>
				{isEmptyState && <AppEmptyState title="No email templates found" description="You don't have any email templates yet" hideButton />}

				{!isEmptyState && (
					<Fragment>
						<div className="email-templates__list">
							<p className="email-templates__list-label">Templates</p>

							<div className="email-templates__list-items">
								{groupedItems.map((group) => (
									<div className="email-templates__category" key={group.category}>
										<button
											type="button"
											className={classNames({ "email-templates__category-header": true, "email-templates__category-header--active": group.category === expandedCategory })}
											onClick={() => onHandleToggleCategory(group.category)}
										>
											<span className="email-templates__category-name">{group.category}</span>
											<AppChevronIcon className={classNames({ "email-templates__category-chevron": true, "email-templates__category-chevron--expanded": group.category === expandedCategory })} />
										</button>

										{group.category === expandedCategory && (
											<div className="email-templates__category-items">
												{group.items.map((item) => (
													<button
														type="button"
														key={item.templateKey}
														className={classNames({ "email-templates__list-item": true, "email-templates__list-item--active": item.templateKey === activeTemplateKey })}
														onClick={() => onHandleSelectTemplate(item.templateKey)}
													>
														<span className="email-templates__list-item-name">{item.displayName}</span>
														{isItemDirty(item.templateKey) && <span className="email-templates__list-item-dot" />}
													</button>
												))}
											</div>
										)}
									</div>
								))}
							</div>
						</div>

						<div className="email-templates__main">
							{!loadingDetail && template && (
								<Fragment>
									<div className="email-templates__header">
										<div className="email-templates__header-text">
											<p className="email-templates__header-title">{template.displayName}</p>
										</div>

										<div className="email-templates__header-actions">
											{viewMode === "editor" ? (
												<AppButton type="button" outline label="Cancel" disabled={saving} onClick={onHandleCancelEdit} />
											) : (
												<AppButton type="button" outline label="Reset" disabled={saving || !template?.customized} onClick={onHandleRequestReset} />
											)}
											<AppButton type="button" label={viewMode === "editor" ? "Save" : "Edit"} disabled={saving} onClick={onHandleToggleEdit} />
										</div>
									</div>

									{confirmingReset && (
										<div className="email-templates__confirm">
											<p className="email-templates__confirm-text">Reset this template to its original default? Your customizations will be discarded.</p>
											<div className="email-templates__confirm-actions">
												<AppButton type="button" outline label="Cancel" disabled={saving} onClick={onHandleCancelReset} />
												<AppButton type="button" label="Confirm" disabled={saving} onClick={onHandleConfirmReset} />
											</div>
										</div>
									)}

									{viewMode === "editor" && (
										<div className="email-templates__editor-pane">
											<Editor height="100%" language="html" value={html} onChange={onHandleChangeHtml} options={{ automaticLayout: true, minimap: { enabled: false }, fontSize: 13, wordWrap: "on", scrollBeyondLastLine: false }} />
										</div>
									)}

									{viewMode === "preview" && (
										<div className="email-templates__preview-panel">
											<div className="email-templates__preview-card">
												{loadingPreview && (
													<div className="email-templates__preview-loading">
														<AppLoadingIndicator />
													</div>
												)}

												<iframe className="email-templates__preview-frame" title="email-template-preview" srcDoc={renderedHtml} sandbox="" />
											</div>
										</div>
									)}
								</Fragment>
							)}
						</div>
					</Fragment>
				)}
			</div>
		</div>
	);
};

export default PageEmailTemplates;
